import {
    AnyFinancialEntity,
    FinancialEntityInfo,
    isLinkedAndConnected,
} from '../../financialEntities';
import { sortBy } from '../../util/arrays';

export interface EligibleTransactionAccountOption {
    fe: AnyFinancialEntity;
}

export interface IneligibleTransactionAccountOption {
    fe: AnyFinancialEntity;
    ineligibleReason: TransactionAccountIneligibleReason;
}

export type TransactionAccountIneligibleReason =
    | 'AGGREGATOR_NOT_CONNECTED'
    | 'GOAL_TO_GOAL_DIFFERENT_ACCOUNT_TYPE'
    | 'GOAL_TO_GOAL_SOURCE_NOT_CASH_PLUS';

export interface SourceOrDestination {
    id: string;
    info: FinancialEntityInfo;
    isFixed?: boolean;
}

export const getTransactionAccountOptions = ({
    transactionAccounts,
    otherSelectedAccount,
    isSource,
}: {
    transactionAccounts: AnyFinancialEntity[];
    otherSelectedAccount?: SourceOrDestination | null;
    isSource: boolean;
}) => {
    const eligible: EligibleTransactionAccountOption[] = [];
    const ineligible: IneligibleTransactionAccountOption[] = [];

    for (const fe of transactionAccounts) {
        // If this matches the other selected account, exclude it completely
        if (fe.id === otherSelectedAccount?.id) {
            continue;
        }

        // If this would be the second external account, exclude it completely
        if (
            fe.type !== 'PlentyGoalAccount' &&
            otherSelectedAccount &&
            otherSelectedAccount.info.feType !== 'PlentyGoalAccount'
        ) {
            continue;
        }

        if (
            // ACH info does not exist and account not connected => ineligible
            !fe.achInfoExists &&
            fe.type !== 'PlentyGoalAccount' &&
            !isLinkedAndConnected(fe)
        ) {
            ineligible.push({
                fe,
                ineligibleReason: 'AGGREGATOR_NOT_CONNECTED',
            });
        } else if (
            // Goal-to-goal transfer with mismatched account types => ineligible
            fe.info.feType === 'PlentyGoalAccount' &&
            otherSelectedAccount?.info.feType === 'PlentyGoalAccount' &&
            fe?.info?.accountType !== otherSelectedAccount.info.accountType
        ) {
            ineligible.push({
                fe,
                ineligibleReason: 'GOAL_TO_GOAL_DIFFERENT_ACCOUNT_TYPE',
            });
        } else if (
            isSource &&
            fe.info.feType === 'PlentyGoalAccount' &&
            otherSelectedAccount?.info.feType === 'PlentyGoalAccount' &&
            fe.info.investmentType !== 'HIGH_YIELD_CASH'
        ) {
            // Source of goal-to-goal transfer not high-yield cash => ineligible
            ineligible.push({
                fe,
                ineligibleReason: 'GOAL_TO_GOAL_SOURCE_NOT_CASH_PLUS',
            });
        } else if (
            !isSource &&
            fe.info.feType === 'PlentyGoalAccount' &&
            otherSelectedAccount?.info.feType === 'PlentyGoalAccount' &&
            otherSelectedAccount.info.investmentType !== 'HIGH_YIELD_CASH'
        ) {
            // Source of goal-to-goal transfer not high-yield cash => ineligible
            ineligible.push({
                fe,
                ineligibleReason: 'GOAL_TO_GOAL_SOURCE_NOT_CASH_PLUS',
            });
        } else {
            // Otherwise => eligible
            eligible.push({ fe });
        }
    }

    return {
        eligible: sortBy(eligible, ({ fe }) => getSortKey(fe)),
        ineligible: sortBy(ineligible, ({ fe }) => getSortKey(fe)),
    };
};

const getSortKey = (fe: AnyFinancialEntity) =>
    `${fe.institution?.name}|${fe.name}|${fe.goal?.name}`;
